import { useState, useEffect, Fragment, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.sass";
import assets from "../../assets";
import useHandler from "./handler";
import { getChapterDisplayLabel } from "../../utils/chapter-mapping";
import { ToggleButton } from "../../role-based-code/toggle-button";
import { QuizHero } from "../../components/quiz-hero";
import { QuizHistroyDetails } from "../quiz-history";
import { CommonPopup } from "../../components/pop-up";
import { AdminRoundResult } from "../../components/admin-round-results";
import { LatexRenderer } from "../../components/latex-renderer";
import { useAntiCheat, AntiCheatModal, AutoSubmittedModal, AntiCheatRulesModal, useLiveQuizLock, LiveQuizFullscreenGuard } from "../../hooks/useAntiCheat";
import { ImageZoomModal, QuizImagePreview } from "../../components/image-zoom-modal";

const PRACTICE_CONTENT = [
  "No time pressure",
  "Instant feedback & explanations",
  "Personalized learning path",
];

const JOIN_ROOM_CONTENT = [
  "Live multiplayer experience",
  "Timer-based challenges",
  "Global leaderboard & scoring",
];

const QuestionCardContent = ({
  q,
  qIndex,
  currentQIndex,
  isFullySubmitted,
  isAnsweredQ,
  isCorrectQ,
  selOpt,
  mode,
  handleOptionSelect,
  showStack,
  isReviewMode,
  setCurrentQIndex,
  setShowPaletteModal,
  questions,
  questionDetails,
  onZoomImage,
}) => {
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const isScrollable = el.scrollHeight > el.clientHeight + 10; // Threshold reduced to 10px
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 15;
      setShowScrollHint(isScrollable && !isAtBottom);
    };

    // Check on mount and resize
    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);

    el.addEventListener("scroll", checkScroll);
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", checkScroll);
    };
  }, [q?.question, q?.options]);

  const handleScrollDown = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      top: 200,
      behavior: "smooth",
    });
  };

  return (
    <Fragment>
      <section className="question-section" ref={scrollRef}>
        <h3 className="question-title" style={{ fontSize: "17px", fontWeight: "700", color: "#111827", lineHeight: "1.45" }}><LatexRenderer text={q?.question} style={{ fontSize: "17px", fontWeight: "700", color: "#111827" }} /></h3>

        {q?.imageUrls && q.imageUrls.length > 0 && (
          <div className="ws_q_image_container">
            {q.imageUrls.map((imgUrl, i) => (
              <QuizImagePreview
                key={i}
                src={imgUrl}
                alt={`Question Diagram ${i + 1}`}
                title={`Question ${qIndex + 1} Diagram`}
                onZoom={onZoomImage}
              />
            ))}
          </div>
        )}

        {(isFullySubmitted || (isAnsweredQ && !mode)) && isCorrectQ ? (
          <div className="answer-feedback correct">
            <span>Correct Answer!</span>
            <div className="feedback_icon correct">✓</div>
          </div>
        ) : (
          ""
        )}

        {(isFullySubmitted || (isAnsweredQ && !mode)) && !isCorrectQ && (
          <section className="explaination_full_wrapper">
            <img src={assets.ic_wrong_close} alt="wrong" />

            <section className="explaination_wrapper">
              <h4>Incorrect</h4>

              <section className="explaination_container">
                <h5>Explanation:</h5>
                <h6><LatexRenderer text={questionDetails[qIndex]?.EXPLANATION} /></h6>
              </section>
            </section>
          </section>
        )}

        <section className="options-section">
          {q?.options?.map((option, index) => (
            <label
              key={index}
              className={`option_quiz ${(() => {
                const isSubmitted = isFullySubmitted || (isAnsweredQ && !mode);

                if (!isSubmitted) {
                  return selOpt === index ? "selected" : "";
                }

                // // Correct answer -> green
                // if (index === q.correctOptionIndex) {
                //   return "option-correct";
                // }

                // // Selected wrong answer -> red
                // if (selOpt === index && index !== q.correctOptionIndex) {
                //   return "option-wrong";
                // }

                if (q?.options[index] === q?.answer) {
                  return "option-correct";
                }

                if (selOpt === index && q?.options[index] !== q?.answer) {
                  return "option-wrong";
                }

                return "";
              })()}`}
            >
              <input
                type="radio"
                name={`quiz-option-${qIndex}`}
                checked={selOpt === index}
                disabled={
                  isFullySubmitted ||
                  (selOpt !== undefined && !mode) ||
                  (showStack && qIndex !== currentQIndex)
                }
                onChange={() => handleOptionSelect(index, qIndex)}
              />
              <section className="option-text" style={{ fontSize: "15px", fontWeight: "600", color: "#111827" }}>
                <span className="option-letter" style={{ fontSize: "16px", fontWeight: "700", color: "#111827" }}>
                  {String.fromCharCode(65 + index)}.{" "}
                </span>
                {option && <LatexRenderer text={option} style={{ fontSize: "15px", fontWeight: "600", color: "#111827" }} />}
                {q?.optionImages?.[String.fromCharCode(65 + index)] && (
                  <div className="ws_opt_img_wrapper">
                    <QuizImagePreview
                      src={q.optionImages[String.fromCharCode(65 + index)]}
                      alt={`Option ${String.fromCharCode(65 + index)}`}
                      title={`Question ${qIndex + 1} - Option ${String.fromCharCode(65 + index)}`}
                      isOption
                      onZoom={onZoomImage}
                    />
                  </div>
                )}
              </section>
            </label>
          ))}
        </section>
      </section>

      <div
        className={`scroll_indicator_container ${showScrollHint ? "visible" : "hidden"}`}
        onClick={handleScrollDown}
      >
        <div className="scroll_hint">
          <span>Scroll for more</span>
          <img src={assets.ic_chevron_down} alt="scroll down" />
        </div>
      </div>

      {!isReviewMode && !isFullySubmitted && (
        <div className="quiz-nav-footer">
          <button
            className="prev_btn_wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex > 0) {
                setCurrentQIndex(qIndex - 1);
              }
            }}
            disabled={qIndex === 0}
          >
            <img src={assets.ic_left_arrow_blue} alt="ic_left_arrow_blue" />
            <h5>Previous Question</h5>
          </button>
          <button
            className={
              qIndex === questions?.length - 1
                ? "end_quiz_btn_inline"
                : "nexts_btn_wrapper"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (qIndex === questions?.length - 1) {
                setShowPaletteModal(true);
              } else {
                setCurrentQIndex(qIndex + 1);
              }
            }}
          >
            <h5>
              {qIndex === questions?.length - 1 ? "End Quiz" : "Next Question"}
            </h5>
            <img src={assets.ic_arrow_white} alt="ic_arrow_white" />
          </button>
        </div>
      )}
    </Fragment>
  );
};

const QuestionPalettePage = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  setCurrentQIndex,
  setShowPaletteModal,
  setIsQuizActive,
  onSubmitQuiz,
  submitLoading,
  setIsReviewMode,
}) => {
  const unansweredCount = questions?.filter(
    (_, i) => selectedAnswers[i] === undefined,
  ).length;

  return (
    <div className="palette_page_container">
      <div className="palette_page_content">
        <div className="palette_modal_header_main">
          <h3>Question Palette</h3>
          <p>
            You have {String(unansweredCount).padStart(2, "0")} unanswered
            questions. Click on any question number to review.
          </p>
        </div>

        <div className="palette_modal_subheader">
          <h4>Question Palette</h4>
          <div className="q_count_badge_premium">
            {questions?.length} Questions
          </div>
        </div>

        <div className="palette_modal_grid">
          {questions.map((_, index) => {
            const isAnsweredQ = selectedAnswers[index] !== undefined;
            const className = isAnsweredQ
              ? "palette_circle answered"
              : "palette_circle unoccupied";

            return (
              <div
                key={index}
                className={className}
                onClick={() => {
                  setCurrentQIndex(index);
                  setShowPaletteModal(false);
                  setIsReviewMode(true);
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </div>
            );
          })}
        </div>

        <div className="palette_modal_legend">
          <div className="legend_item_premium">
            <div className="status_indicator answered"></div>
            <span>Answered</span>
          </div>
          <div className="legend_item_premium">
            <div className="status_indicator unoccupied"></div>
            <span>Unanswered</span>
          </div>
        </div>

        <div className="palette_modal_actions">
          <button
            className="p_cancel_btn"
            onClick={() => setShowPaletteModal(false)}
          >
            Cancel
          </button>
          <button
            className="p_submit_btn"
            onClick={() => {
              setShowPaletteModal(false);
              setIsQuizActive(false);
              onSubmitQuiz();
              setIsReviewMode(true);
            }}
          >
            {submitLoading && <div className="btn_spinner"></div>}
            Submit Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

const QuestionPaletteSidebar = ({
  questions,
  selectedAnswers,
  getQuestionStatus,
  currentQIndex,
  setCurrentQIndex,
  isQuizSubmitted,
  onSubmitQuiz,
  submitLoading,
}) => {
  return (
    <section className="palette_sidebar_premium">
      <div className="sidebar_palette_header">
        <span>Question Palette</span>
        <div className="q_badge_mini">{questions?.length} Questions</div>
      </div>

      <div className="sidebar_palette_grid">
        {questions.map((_, index) => {
          const status = getQuestionStatus(index);
          const isAnsweredQ = selectedAnswers[index] !== undefined;

          let className = "palette_circle_mini";
          if (isAnsweredQ) {
            className += " answered";
            if (index === currentQIndex) className += " current_answered";
          } else if (index === currentQIndex) {
            className += " current";
          } else if (status === "not-answered") {
            className += " not_answered";
          } else {
            className += " unoccupied";
          }

          return (
            <div
              key={index}
              className={className}
              onClick={() => setCurrentQIndex(index)}
            >
              {String(index + 1).padStart(2, "0")}
            </div>
          );
        })}
      </div>

      <div className="sidebar_status_legend">
        <div className="legend_item_mini">
          <div className="status_dot current"></div>
          <span>Current</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot answered"></div>
          <span>Answered</span>
        </div>
        <div className="legend_item_mini">
          <div className="status_dot unoccupied"></div>
          <span>Unanswered</span>
        </div>
      </div>
      {!isQuizSubmitted && (
        <button
          style={{
            width: "100%",
            marginTop: "auto",
            background: "#0f172a",
            color: "#fff",
            padding: "14px",
            borderRadius: "8px",
            fontWeight: "bold",
            cursor: "pointer",
            border: "none",
          }}
          onClick={onSubmitQuiz}
        >
          {submitLoading ? "Submitting..." : "Submit Quiz"}
        </button>
      )}
    </section>
  );
};

const QuizHSlider = ({
  questions,
  renderCard,
  currentQIndex,
  setCurrentQIndex,
  onEndQuiz,
  endIcon,
}) => {
  const trackRef = useRef(null);
  const rafIdRef = useRef(null); // cancel in-flight rAF
  const cooldownRef = useRef(false); // brief lock between card transitions
  const wheelAccRef = useRef(0); // accumulated deltaX within one gesture
  const wheelTimerRef = useRef(null); // debounce to reset accumulator on silence
  const [isReady, setIsReady] = useState(false);

  // easeOutCubic — instant start, smooth deceleration (iOS-like feel)
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  // Interruptible rAF animator — if called mid-animation, starts from current position
  const animateTo = useCallback((track, targetLeft) => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    const start = track.scrollLeft;
    const dist = targetLeft - start;
    if (Math.abs(dist) < 1) {
      cooldownRef.current = false;
      return;
    }
    const duration = 360;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        rafIdRef.current = requestAnimationFrame(step);
      } else {
        track.scrollLeft = targetLeft;
        rafIdRef.current = null;
        // release cooldown slightly after settle so fast swipes feel responsive
        setTimeout(() => {
          cooldownRef.current = false;
        }, 80);
      }
    };
    rafIdRef.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Center a card index in the track
  const goToIndex = useCallback(
    (idx, track) => {
      const t = track || trackRef.current;
      if (!t) return;
      const card = t.children[idx];
      if (!card) return;
      const target =
        t.clientWidth > 0
          ? card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2
          : idx === 0
            ? 0
            : card.offsetLeft;
      animateTo(t, Math.max(0, target));
    },
    [animateTo],
  );

  // External index change (palette click, dots)
  useEffect(() => {
    setIsReady(false);
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    const timer = setTimeout(() => {
      setIsReady(true);
      cooldownRef.current = false;
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  useEffect(() => {
    if (!isReady) return;
    cooldownRef.current = true;
    goToIndex(currentQIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQIndex]);

  // IntersectionObserver — keep dots/counter in sync while CSS snap or touch settles
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isReady || cooldownRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.85) {
            const idx = Array.from(track.children).indexOf(entry.target);
            if (idx >= 0) setCurrentQIndex(idx);
          }
        });
      },
      { root: track, threshold: 0.85 },
    );
    Array.from(track.children).forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, [questions.length, setCurrentQIndex]);

  // Wheel — vertical (deltaY) scrolls card content naturally.
  // Only horizontal trackpad swipe (deltaX) navigates between cards.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onWheel = (e) => {
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      // Pure vertical wheel — never intercept, let browser scroll the card inner
      if (absY >= absX) return;
      e.preventDefault();
      // Debounce: reset accumulator 200ms after gesture ends
      clearTimeout(wheelTimerRef.current);
      wheelTimerRef.current = setTimeout(() => {
        wheelAccRef.current = 0;
      }, 200);
      // While animating, swallow all events without accumulating
      if (cooldownRef.current) return;
      wheelAccRef.current += e.deltaX;
      // Require 60px of intentional scroll before advancing one card
      if (Math.abs(wheelAccRef.current) < 60) return;
      const direction = wheelAccRef.current > 0 ? 1 : -1;
      wheelAccRef.current = 0;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          direction > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  // Arrow keys — Left/Right to navigate cards
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      const track = trackRef.current;
      setCurrentQIndex((prev) => {
        const next =
          e.key === "ArrowRight"
            ? Math.min(prev + 1, maxIdx)
            : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [questions.length, goToIndex, setCurrentQIndex]);

  // Touch swipe — horizontal swipe of 30px+ advances one card
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let startX = 0,
      startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 30) return;
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      const maxIdx = questions.length - 1;
      setCurrentQIndex((prev) => {
        const next =
          dx > 0 ? Math.min(prev + 1, maxIdx) : Math.max(prev - 1, 0);
        if (next !== prev) goToIndex(next, track);
        else {
          setTimeout(() => {
            cooldownRef.current = false;
          }, 80);
        }
        return next;
      });
    };
    track.addEventListener("touchstart", onTouchStart, { passive: true });
    track.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      track.removeEventListener("touchstart", onTouchStart);
      track.removeEventListener("touchend", onTouchEnd);
    };
  }, [questions.length, goToIndex, setCurrentQIndex]);

  return (
    <section className="quiz_hslider_wrapper">
      <div className="quiz_hslider_track" ref={trackRef}>
        {questions.map((_, qIndex) => (
          <div
            className={`quiz_hslider_card${qIndex === currentQIndex ? " is_active" : ""}`}
            key={qIndex}
          >
            <div className="quiz_hslider_card_inner">{renderCard(qIndex)}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const QuizJoinInitialContent = ({
  waitingCall,
  roomCode,
  nickName,
  setWaitingCall,
  onChangeRoom,
  joinRoom,
  setJoinQuiz,
}) => {
  const navigate = useNavigate();
  const isValid = (nickName || "").trim().length > 0 && (roomCode || "").trim().length > 0;

  const handleJoinClick = () => {
    try {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen request error:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } catch (e) {
      console.log("Fullscreen error:", e);
    }
    document.body.classList.add("hide_sidebar");
    joinRoom();
  };

  return (
    <section className="join_quiz_entry_wrapper">
      <button
        className="join_quiz_back_btn"
        onClick={() => {
          setJoinQuiz(false);
          setWaitingCall(false);
          document.body.classList.remove("hide_sidebar");
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
          }
          const currentPath = window.location.pathname;
          const basePath = currentPath.split("/quiz")[0];
          const targetDashboardRoute = basePath === "" ? "/dashboard" : `${basePath}/dashboard`;
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate(targetDashboardRoute);
          }
        }}
      >
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.5"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span>Back</span>
      </button>

      <div className="join_quiz_icon_badge">
        <img src={assets.ic_join_rocket} alt="Rocket Icon" />
      </div>

      <h3 className="join_quiz_title">Join the Quiz</h3>
      <p className="join_quiz_subtitle">Enter the room code shared by your teacher to jump right in.</p>

      <div className="join_quiz_form">
        <div className="join_quiz_input_container">
          <label className="join_quiz_label">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span>NICKNAME</span>
          </label>
          <div className="join_quiz_input_wrapper">
            <img src={assets.ic_mood_emoji} alt="ic_mood_emoji" />
            <input
              placeholder="e.g. QuizMaster99"
              name="nickName"
              value={nickName}
              onChange={onChangeRoom}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="join_quiz_input_container">
          <label className="join_quiz_label">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span>ROOM CODE</span>
          </label>
          <div className="join_quiz_input_code_wrapper">
            <input
              placeholder="e.g. 185693"
              name="roomCode"
              value={roomCode}
              onChange={onChangeRoom}
              maxLength={6}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
            />
          </div>
        </div>

        <button
          className={`practice_btn_wrapper ${isValid ? "active" : ""}`}
          onClick={handleJoinClick}
        >
          <span>Join Room</span>
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </section>
  );
};

const QuizLobbyContent = ({
  players,
  roomCode,
  nickName,
  setJoinQuiz,
  setWaitingCall,
}) => {
  return (
    <section className="chat_join_lobby_container">
      <div className="lobby_hero_card">
        <div className="chat_lobby_room_container">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <span className="room_code_label">ROOM CODE:</span>
          <span className="room_code_val">{roomCode}</span>
        </div>

        <div className="round_container">
          <div className="first_round_cricle">
            <div className="second_round_cricle">
              <div className="third_round_cricle">
                <h1>{nickName?.charAt(0)?.toUpperCase()}</h1>
              </div>
            </div>
          </div>
        </div>

        <h2 className="nick_name_text_wrapper">
          {nickName?.charAt(0)?.toUpperCase() + nickName?.slice(1)}
        </h2>

        <div className="room_loading_content">
          <div className="orange_dot_loader">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <h6>Waiting for teacher to start...</h6>
        </div>

        <button
          className="leave_room_btn_container"
          onClick={() => {
            setJoinQuiz(false);
            setWaitingCall(false);
            document.body.classList.remove("hide_sidebar");
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
            }
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Leave Room</span>
        </button>
      </div>

      <div className="chat_lobby_static_wrapper">
        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge tip">
            <img src={assets.ic_quick_tip} alt="Quick Tip" />
          </div>
          <div className="lobby_card_text">
            <h4>Quick Tip</h4>
            <p>
              Read all options carefully before answering. Some questions might
              have tricky wording!
            </p>
          </div>
        </div>

        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge fair">
            <img src={assets.ic_fair_play} alt="Fair Play" />
          </div>
          <div className="lobby_card_text">
            <h4>Fair Play</h4>
            <p>
              Keep your answers to yourself. Points are awarded for speed and
              accuracy.
            </p>
          </div>
        </div>

        <div className="lobby_static_card_wrapper">
          <div className="lobby_card_icon_badge wifi">
            <img src={assets.ic_wifi_green} alt="Connection Stable" />
          </div>
          <div className="lobby_card_text">
            <h4>Connection Stable</h4>
            <p>Ping: 24ms. You're fully synced with the host and ready to go.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const QuizReviewContent = ({
  questions,
  selectedAnswers,
  setAsyncCurrentIndex,
  setShowReviewPage,
  setEditingFromReview,
  onSubmitQuiz,
  showReviewPage,
  asyncQuizSubmit,
}) => {
  const totalQ = questions?.length || 0;
  const answeredCount = Object.keys(selectedAnswers || {}).filter(
    (k) => selectedAnswers[k] !== undefined
  ).length;
  const unansweredCount = totalQ - answeredCount;

  const sanitizeImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    let trimmed = url
      .trim()
      .replace(/(%22|%27|["',\\\/])+$/gi, "")
      .replace(/^(%22|%27|["'\\\/])+/gi, "")
      .trim();
    if (
      !trimmed ||
      trimmed === "null" ||
      trimmed === "undefined" ||
      trimmed === "none" ||
      trimmed === "[]" ||
      trimmed === "{}"
    ) {
      return "";
    }
    // If it's already a full URL from the question API, return it directly!
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:image/") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }
    if (trimmed.startsWith("question_assets/")) {
      return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${trimmed}`;
    } else if (trimmed.startsWith("/question_assets/")) {
      return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
    } else if (trimmed.includes("question_assets")) {
      const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
      return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${assetPart}`;
    } else if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
      return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api${trimmed}`;
    }
    return trimmed;
  };

  const isImgUrl = (str) => {
    if (!str || typeof str !== "string") return false;
    const s = str.trim();
    return (
      s.startsWith("http://") ||
      s.startsWith("https://") ||
      s.startsWith("data:image/") ||
      s.startsWith("blob:") ||
      s.startsWith("/") ||
      s.includes("question_assets") ||
      /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(s)
    );
  };

  const handleImageError = (e) => {
    // Non-destructive fallback handler
    console.warn("Review image load error:", e?.target?.src);
  };

  return (
    <section className="quiz_review_wrapper">
      <div className="quiz_review_container">
        {/* Top Header Card */}
        <div className="quiz_review_header">
          <div className="quiz_review_header_left">
            <div className="quiz_review_icon_badge">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3>Review Your Answers</h3>
              <p>Review all options before final submission. You can edit any answer.</p>
            </div>
          </div>

          <div className="quiz_review_stats">
            <div className="review_stat_pill answered">
              <span className="dot"></span>
              <span>{answeredCount} Answered</span>
            </div>
            <div className="review_stat_pill unanswered">
              <span className="dot"></span>
              <span>{unansweredCount} Unanswered</span>
            </div>
          </div>
        </div>

        {/* List of Question Review Cards */}
        <div className="quiz_review_cards_list">
          {questions?.map((question, index) => {
            const selectedValue = selectedAnswers[index];
            const isAnswered = selectedValue !== undefined && selectedValue !== null && String(selectedValue).trim() !== "";
            const isShort = question?.isShort || question?.type === "SHORT" || question?.questionType === "SHORT";

            let selectedIndex = null;
            let optionLetter = null;
            if (typeof selectedValue === "number") {
              selectedIndex = selectedValue;
              optionLetter = String.fromCharCode(65 + selectedValue);
            } else if (typeof selectedValue === "string" && selectedValue.trim() !== "") {
              const valTrim = selectedValue.trim();
              if (/^[0-9]+$/.test(valTrim)) {
                selectedIndex = parseInt(valTrim, 10);
                optionLetter = String.fromCharCode(65 + selectedIndex);
              } else if (/^[A-E]$/i.test(valTrim)) {
                optionLetter = valTrim.toUpperCase();
                selectedIndex = optionLetter.charCodeAt(0) - 65;
              } else {
                const foundIdx = question?.options?.findIndex(
                  (opt) => typeof opt === "string" && opt.trim().toLowerCase() === valTrim.toLowerCase()
                );
                if (foundIdx !== -1 && foundIdx !== undefined) {
                  selectedIndex = foundIdx;
                  optionLetter = String.fromCharCode(65 + foundIdx);
                }
              }
            }

            const selectedOptionText = !isShort && selectedIndex !== null ? question?.options?.[selectedIndex] : null;

            const optImgCandidate = !isShort && optionLetter
              ? question?.optionImages?.[optionLetter] ||
                question?.optionImages?.[optionLetter.toLowerCase()] ||
                question?.option_images?.[optionLetter] ||
                question?.option_images?.[optionLetter.toLowerCase()] ||
                question?.OPTION_IMAGES?.[optionLetter] ||
                question?.OPTION_IMAGES?.[optionLetter.toLowerCase()] ||
                (selectedIndex !== null && question?.options?.[selectedIndex] && isImgUrl(question.options[selectedIndex]) ? question.options[selectedIndex] : null) ||
                (selectedIndex !== null && question?.CHOICES?.[selectedIndex]?.image_url ? question.CHOICES[selectedIndex].image_url : null)
              : null;

            const selectedOptionImg = optImgCandidate
              ? (typeof optImgCandidate === "object"
                  ? (sanitizeImageUrl(optImgCandidate.url || optImgCandidate.src) || optImgCandidate.url || optImgCandidate.src)
                  : (sanitizeImageUrl(optImgCandidate) || optImgCandidate))
              : null;

            const isTextAnImg = typeof selectedOptionText === "string" && isImgUrl(selectedOptionText);
            const cleanOptText = isTextAnImg ? "" : (selectedOptionText || "");

            const rawQImages = (
              Array.isArray(question?.IMAGE_URLS) && question.IMAGE_URLS.length > 0
                ? question.IMAGE_URLS
                : Array.isArray(question?.image_urls) && question.image_urls.length > 0
                ? question.image_urls
                : Array.isArray(question?.imageUrls) && question.imageUrls.length > 0
                ? question.imageUrls
                : Array.isArray(question?.question_images) && question.question_images.length > 0
                ? question.question_images
                : Array.isArray(question?.images) && question.images.length > 0
                ? question.images
                : question?.IMAGE_URL || question?.image_url || question?.imageUrl || question?.image || question?.diagram || question?.question_image
                ? [question?.IMAGE_URL || question?.image_url || question?.imageUrl || question?.image || question?.diagram || question?.question_image]
                : []
            );

            const questionDiagrams = rawQImages
              .map((img) => {
                if (!img) return "";
                let raw = typeof img === "object" ? (img.url || img.src || img.image_url || img.image || "") : img;
                if (typeof raw !== "string" || !raw.trim()) return "";
                return sanitizeImageUrl(raw) || raw;
              })
              .filter(Boolean);

            return (
              <div
                key={question?.questionId || index}
                className={`quiz_review_card ${isAnswered ? "is_answered" : "is_unanswered"}`}
              >
                <div className="review_card_header">
                  <div className="review_q_info">
                    <span className="review_q_badge">Q{index + 1}</span>
                    {isAnswered ? (
                      <span className="status_tag answered">{isShort ? "Answer Entered" : "Answered"}</span>
                    ) : (
                      <span className="status_tag unanswered">Not Answered</span>
                    )}
                  </div>

                  <button
                    className="review_edit_btn"
                    onClick={() => {
                      if (setEditingFromReview) setEditingFromReview(true);
                      setAsyncCurrentIndex(index);
                      setShowReviewPage(false);
                    }}
                  >
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span>{isAnswered ? "Edit Answer" : "Answer Now"}</span>
                  </button>
                </div>

                <h4 className="review_q_text">
                  <LatexRenderer text={question?.question || question?.question_text || ""} />
                </h4>

                {questionDiagrams.length > 0 && (
                  <div className="review_q_image_container">
                    {questionDiagrams.map((imgUrl, i) => (
                      <img
                        key={i}
                        src={imgUrl}
                        alt={`Question Diagram ${i + 1}`}
                        className="review_q_diagram_img"
                        onError={handleImageError}
                      />
                    ))}
                  </div>
                )}

                <div className="review_ans_box">
                  {isAnswered ? (
                    <div className="review_ans_filled_box">
                      {isShort ? (
                        <div className="review_ans_filled_text" style={{ padding: "8px 12px", fontWeight: 600, color: "#1F2937" }}>
                          Answer: <LatexRenderer text={String(selectedValue)} />
                        </div>
                      ) : (
                        <>
                          <div className="review_ans_badge_row">
                            <span className="review_ans_opt_badge">{optionLetter}</span>
                            {cleanOptText && cleanOptText.trim() !== "" && (
                              <div className="review_ans_filled_text">
                                <LatexRenderer text={cleanOptText} />
                              </div>
                            )}
                          </div>

                          {selectedOptionImg && (
                            <div className="review_ans_img_wrapper">
                              <img
                                src={selectedOptionImg}
                                alt={`Option ${optionLetter}`}
                                className="review_ans_diagram_img"
                                onError={handleImageError}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="review_ans_empty">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span>{isShort ? "No answer entered yet" : "No answer selected yet"}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Ready to Submit Card */}
        <div className="quiz_review_footer_bar">
          <div className="review_footer_info">
            <h3>Ready to submit?</h3>
            <p>You answered {answeredCount} out of {totalQ} questions.</p>
          </div>

          <button className="quiz_review_submit_btn" onClick={asyncQuizSubmit}>
            <span>Submit Quiz</span>
            <div className="submit_btn_icon">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export const Quiz = () => {
  const {
    user,
    openDropdown,
    subjectRef,
    chapterRef,
    questionRef,
    selectedChapter,
    selectedSubject,
    panelOpen,
    chapters,
    subjects,
    isStartEnabled,
    currentQuestion,
    selectedOption,
    questions,
    currentQIndex,
    questionDetails,
    isAnswered,
    isCorrect,
    markDetails,
    showMarks,
    setShowMarks,
    isQuizActive,
    isLoading,
    selectChapters,
    hidePanals,
    mode,
    numberOfQuestion,
    selectedQuestion,
    submitLoading,
    timeRemaining,
    quizInitial,
    joinQuiz,
    waitingCall,
    roomCode,
    nickName,
    players,
    wsQuestion,
    wsQuestionIndex,
    wsTotalQuestions,
    wsLeaderboard,
    wsFinalScores,
    wsParticipants,
    wsQuizStarted,
    wsQuizEnded,
    wsSelectedAnswer,
    wsQuizSubmitted,
    wsReviewMode,
    wsQuizFinalResults,
    isAsyncQuiz,
    asyncCurrentQuestion,
    asyncCurrentIndex,
    asyncSelectedAnswers,
    showReviewPage,
    asyncQuestions,
    leaderboardData,
    showAsyncLeaderboard,
    asyncLeaderboard,
    quizHistorySessions,
    showAsyncHistory,
    onSubmitFinalQuiz,
    wsTimeRemaining,
    wsSubmitted,
    showAdminRoundResult,
    adminRoundResult,
    setShowAdminRoundResult,
    setWsSubmitted,
    setRoomDetails,
    setAsyncQuestions,
    setAsyncSelectedAnswers,
    setShowReviewPage,
    setAsyncLeaderboard,
    setShowAsyncLeaderboard,
    setIsAsyncQuiz,
    setWsTimeRemaining,
    handleWsAnswer,
    setWsFinalScores,
    setWsQuestion,
    setWsQuizStarted,
    asyncQuizSubmit,
    apiJoinRoomHistoryDetails,
    handleAsyncAnswer,
    handleAsyncNext,
    joinRoom,
    setShowAsyncHistory,
    setQuizInitial,
    setJoinQuiz,
    setWsQuizEnded,
    setWsQuizSubmitted,
    setWsReviewMode,
    setWsSelectedAnswer,
    setPlayers,
    onChangeRoom,
    setWaitingCall,
    setTimeRemaining,
    setSelectedChapters,
    setCurrentQIndex,
    setIsReviewMode,
    isReviewMode,
    setPanelOpen,
    setOpenDropdown,
    setSelectedSubject,
    setSelectedQuestion,
    onStartQuiz,
    handleNext,
    handlePrevious,
    handleOptionSelect,
    onSubmitQuiz,
    onCancel,
    onRetakeQuiz,
    setIsQuizActive,
    getQuizDuration,
    setHidePanals,
    onToggleQuizBtn,
    getQuestionStatus,
    setAsyncCurrentIndex,
    showSetupPopup,
    setShowSetupPopup,
    selectedAnswers,
    showPaletteModal,
    setShowPaletteModal,
    editingFromReview,
    setEditingFromReview,
  } = useHandler();

  const [showLowTimeWarning, setShowLowTimeWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const warnedThresholds = useRef(new Set());
  const [slideDirection, setSlideDirection] = useState("right");
  const [animKey, setAnimKey] = useState(0);

  const [zoomModalImage, setZoomModalImage] = useState(null);

  const [flaggedQuestions, setFlaggedQuestions] = useState([3, 7]); // sample flagged q indices

  const isAntiCheatActive =
    (isAsyncQuiz && !showReviewPage && !showAsyncLeaderboard) ||
    (isQuizActive && !showReviewPage && !showMarks && !showAsyncLeaderboard && !showAsyncHistory);

  // Silent fullscreen lock & key restriction for Live Quiz (no popups, no warnings, no auto-submit)
  const { isNeedsFullscreen, tryFullscreen } = useLiveQuizLock(wsQuizStarted && !wsQuizEnded && !wsSubmitted);

  const {
    warnings: antiCheatWarnings,
    showWarningModal: showAntiCheatModal,
    warningMessage: antiCheatMessage,
    isAutoSubmitted,
    autoSubmitReason,
    showRulesModal,
    acknowledgeRules,
    reEnterFullscreen,
    closeAutoSubmitModal,
  } = useAntiCheat({
    isActive: isAntiCheatActive,
    maxWarnings: 3,
    onAutoSubmit: () => {
      if (isAsyncQuiz) {
        asyncQuizSubmit();
      }
    },
  });

  const totalQuestions = questions?.length;
  const isFullySubmitted = !!markDetails?.DETAILS?.length;

  const showJoinScreen =
    joinQuiz &&
    !waitingCall &&
    !wsQuizStarted &&
    !asyncCurrentQuestion &&
    !wsQuestion &&
    !showAsyncLeaderboard;

  const handleSubmitQuiz = () => {
    // Handle quiz submission
    alert("Quiz submitted!");
  };

  // useEffect(() => {
  //   if (!showMarks) {
  //     setTimeRemaining(QUIZ_DURATION);
  //   }
  // }, [showMarks]);

  useEffect(() => {
    const isTestActive =
      waitingCall ||
      wsQuizStarted ||
      isAsyncQuiz ||
      isQuizActive ||
      showAsyncLeaderboard ||
      showReviewPage ||
      wsQuizEnded ||
      showAsyncHistory ||
      (asyncLeaderboard && asyncLeaderboard.length > 0);

    if (isTestActive) {
      document.body.classList.add("hide_sidebar");
    } else {
      document.body.classList.remove("hide_sidebar");
    }

    return () => {
      document.body.classList.remove("hide_sidebar");
    };
  }, [
    waitingCall,
    wsQuizStarted,
    isAsyncQuiz,
    isQuizActive,
    showAsyncLeaderboard,
    showReviewPage,
    wsQuizEnded,
    showAsyncHistory,
    asyncLeaderboard,
  ]);

  useEffect(() => {
    if (questions.length > 0 && isQuizActive) {
      const duration = getQuizDuration(selectedQuestion);
      setTimeRemaining(duration);
    }
  }, [questions.length, isQuizActive]);

  useEffect(() => {
    if (!isQuizActive || questions.length === 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsQuizActive(false);
          onSubmitQuiz();
          return 0;
        }

        // Low time warnings
        const totalDuration = getQuizDuration(selectedQuestion);
        const thresholds = [
          { time: 600, msg: "10 minutes left! Stay focused." },
          { time: 300, msg: "Only 5 minutes left! Keep going." },
          {
            time: 60,
            msg: "Warning: 1 minute remaining! Finalize your answers.",
          },
          { time: 30, msg: "Hurry! 30 seconds left!" },
        ];

        const currentThreshold = thresholds.find(
          (t) => prev === t.time && t.time < totalDuration,
        );
        if (
          currentThreshold &&
          !warnedThresholds.current.has(currentThreshold.time)
        ) {
          setWarningMessage(currentThreshold.msg);
          setShowLowTimeWarning(true);
          warnedThresholds.current.add(currentThreshold.time);
          setTimeout(() => setShowLowTimeWarning(false), 5000);
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizActive, questions.length]);

  useEffect(() => {
    if (!wsQuizStarted || !wsQuestion) return;

    setWsTimeRemaining(15);

    const timer = setInterval(() => {
      setWsTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);

          // auto submit only once
          if (!wsSubmitted && wsSelectedAnswer !== null) {
            handleWsAnswer(wsSelectedAnswer);
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [wsQuestionIndex, wsSubmitted, wsSelectedAnswer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const correctCount = markDetails?.DETAILS?.filter(
    ({ IS_CORRECT }) => IS_CORRECT === true,
  ).length;
  const wrongCount = markDetails?.DETAILS?.filter(
    ({ USER_ANSWER, IS_CORRECT }) => USER_ANSWER && IS_CORRECT === false,
  ).length;
  const notAttemptedCount = markDetails?.DETAILS?.filter(
    (USER_ANSWER) => !USER_ANSWER,
  ).length;

  const notAnswerdCount = markDetails?.NOT_ATTEMPTED_COUNT;

  const scorePercentage = Math.round(
    (markDetails?.TOTAL_MARKS / markDetails?.MAX_MARKS) * 100,
  );

  const renderScoreCardDetails = (item, index) => {
    const {
      QUESTION,
      IS_CORRECT,
      USER_ANSWER,
      CORRECT_ANSWER,
      REMARK,
      NOT_ATTEMPTED,
    } = item || {};

    return (
      <Fragment key={index}>
        {IS_CORRECT === true ? (
          <section className="questions_container1">
            <div className="status_icon_wrapper correct">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <section className="questions_content">
              <h4>Question {index + 1}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <div className="ans_comparison_grid">
                <div className="ans_card correct_answer">
                  <span className="label">Correct Answer</span>
                  <div className="value"><LatexRenderer text={CORRECT_ANSWER} /></div>
                </div>
              </div>
              {REMARK && (
                <div className="ans_box explanation">
                  <span className="label">Remark</span>
                  <div className="value"><LatexRenderer text={REMARK} /></div>
                </div>
              )}
            </section>
          </section>
        ) : (
          <section
            className={`${
              NOT_ATTEMPTED
                ? "questions_container_not_attempted"
                : "questions_container3"
            }`}
          >
            <div
              className={`status_icon_wrapper ${IS_CORRECT === false ? "wrong" : ""}`}
            >
              {NOT_ATTEMPTED ? (
                <img src={assets.ic_unattempt} alt="wrong" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
            </div>
            <section className="questions_content">
              <h4>Question {index + 1}</h4>
              <h5><LatexRenderer text={QUESTION} /></h5>
              <section className="ans_comparison_grid">
                <div className="ans_card your_answer">
                  <span className="label">Your Answer</span>
                  <div className="value"><LatexRenderer text={USER_ANSWER || "Not Attempted"} /></div>
                </div>
                <div className="ans_card correct_answer">
                  <span className="label">Correct Answer</span>
                  <div className="value"><LatexRenderer text={CORRECT_ANSWER} /></div>
                </div>
              </section>
              {REMARK && (
                <div className="ans_box explanation">
                  <span className="label">Explanation</span>
                  <div className="value"><LatexRenderer text={REMARK} /></div>
                </div>
              )}
            </section>
          </section>
        )}
      </Fragment>
    );
  };

  const QuizSetupPopup = () => {
    const durationMin = getQuizDuration(selectedQuestion) / 60;
    const qCount = selectedQuestion;

    return (
      <div className="quiz_setup_overlay">
        <div className="quiz_setup_card">
          <div className="quiz_setup_header">
            <div>
              <h3>Quiz Configuration</h3>
              <p>Review your selection before beginning</p>
            </div>
          </div>

          <div className="quiz_setup_body">
            <div className="setup_section">
              <div className="setup_icon_wrapper subject_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Subject</span>
                <h4 className="setup_value">{selectedSubject}</h4>
              </div>
            </div>

            <div className="setup_section">
              <div className="setup_icon_wrapper chapter_p_icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="setup_info">
                <span className="setup_label">Selected Chapters</span>
                <ul className="setup_list_p">
                  {(selectChapters || []).map((chapter, idx) => (
                    <li key={idx}>{chapter}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="setup_stats_row">
              <div className="setup_stat_box questions_box_p">
                <div className="stat_icon quiz_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">QUESTIONS</span>
                  <span className="stat_value">{qCount}</span>
                </div>
              </div>

              <div className="setup_stat_box duration_box_p">
                <div className="stat_icon time_icon_p">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="stat_labels">
                  <span className="stat_label">DURATION</span>
                  <span className="stat_value">{durationMin}min</span>
                </div>
              </div>
            </div>

            <div className="setup_note_p">
              <div className="note_icon_p">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>
                Once you start the quiz, the timer will begin automatically.
                Make sure you're ready!
              </p>
            </div>
          </div>

          <div className="quiz_setup_footer">
            <button
              className="setup_cancel_btn"
              onClick={() => setShowSetupPopup(false)}
            >
              Cancel
            </button>
            <button
              className="setup_start_btn"
              onClick={() => {
                setShowSetupPopup(false);
                setHidePanals(true);
                setIsQuizActive(true);
                onStartQuiz();
              }}
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      {showSetupPopup && <QuizSetupPopup />}

      {/* Low Time Warning Toast */}
      <div className={`low_time_toast ${showLowTimeWarning ? "show" : ""}`}>
        <div className="toast_content">
          <div className="toast_icon">
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="toast_text">
            <span>{warningMessage}</span>
          </div>
          <button
            className="toast_close"
            onClick={() => setShowLowTimeWarning(false)}
          >
            ×
          </button>
        </div>
      </div>

      <section className="chat_layout">
        {!quizInitial && !joinQuiz && !hidePanals && (
          <section className="nav_context_contanier">
            <section className="nav_learn_title">
              <h4>Learning Context</h4>
              <h6>Set your topic to start learning</h6>
            </section>

            <section className="nav_subject_details">
              <section className="board_wrapper">
                <section className="class">
                  <h5>Competitive Exam</h5>
                  <h4>{user?.data?.focus}</h4>
                </section>
              </section>
            </section>

            <section className="quiz_nav_mode_wrapper">
              <section className="quiz_nav_mode_container">
                <h4>Instant Mode</h4>
                <ToggleButton isToggle={mode} onToggle={onToggleQuizBtn} />
                <h4>Review Mode</h4>
              </section>
            </section>

            <section className="nav_subject_wrapper">
              {/* MEDIUM */}

              {/* SUBJECT */}
              <section
                className="subject_wrapper"
                ref={subjectRef}
                onClick={() => setOpenDropdown("subject")}
              >
                <h4>Subject</h4>
                <section className="drop_down_wrapper">
                  <p>{selectedSubject || "Select Subject"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "subject" && (
                  <section className="options_list">
                    {isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading subjects...
                      </p>
                    ) : subjects?.length > 0 ? (
                      subjects?.map((item, i) => (
                        <p
                          key={i}
                          className="option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSubject(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No subjects found
                      </p>
                    )}
                  </section>
                )}
              </section>

              {/* CHAPTER */}
              <section
                className="subject_wrapper"
                ref={chapterRef}
                onClick={() => setOpenDropdown("chapter")}
              >
                <h4>Chapter</h4>
                <section className="drop_down_wrapper">
                  {/* <p>  {selectChapters.length > 0  ? selectChapters.join(", ") : "Select Chapter"}</p> */}
                  <section className="selected_chapter_list">
                    {selectChapters?.length === 0 ? (
                      <p>Select Chapter</p>
                    ) : (
                      selectChapters?.map((item, index) => {
                        return (
                          <section key={index} className="items">
                            <p>{getChapterDisplayLabel(item)}</p>
                            <img
                              src={assets.ic_close_white}
                              alt="ic_close_white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedChapters((prev) =>
                                  prev.filter((c) => c !== item),
                                );
                              }}
                              style={{
                                cursor: "pointer",
                                width: "14px",
                                height: "14px",
                              }}
                            />
                          </section>
                        );
                      })
                    )}
                  </section>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "chapter" && (
                  <section className="options_list">
                    {!selectedSubject ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Subject{" "}
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading chapters...
                      </p>
                    ) : chapters?.length > 0 ? (
                      chapters?.map((item, i) => (
                        <p
                          key={i}
                          className={`option_item ${selectChapters.includes(item) ? "selected" : ""}`}
                          onClick={(e) => {
                            setSelectedChapters((prev) => {
                              // If clicked item is "All Chapters"
                              if (item === "All Chapters") {
                                // If already selected â†’ remove it
                                if (prev.includes("All Chapters")) {
                                  return [];
                                }
                                // Else â†’ only select "All Chapters"
                                return ["All Chapters"];
                              }

                              // If selecting individual chapter â†’ remove "All Chapters"
                              let updated = prev.filter(
                                (c) => c !== "All Chapters",
                              );

                              if (updated.includes(item)) {
                                // remove if already selected
                                updated = updated.filter((c) => c !== item);
                              } else {
                                // add new chapter
                                updated = [...updated, item];
                              }

                              return updated;
                            });
                          }}
                        >
                          {item === "All Chapters"
                            ? "All Chapters"
                            : getChapterDisplayLabel(item)}
                          {selectChapters.includes(item) && ` \u2713`}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No chapters found
                      </p>
                    )}
                  </section>
                )}
              </section>
              {/* LANGUAGE */}
              {/* NO OF QUESTION */}
              <section
                className="subject_wrapper"
                ref={questionRef}
                onClick={() => setOpenDropdown("question")}
              >
                <h4>Number Of Question</h4>
                <section className="drop_down_wrapper">
                  <p>{selectedQuestion || "Select Question"}</p>
                  <img src={assets.ic_chevron_down} alt="ic_chevron_down" />
                </section>
                {openDropdown === "question" && (
                  <section className="options_list">
                    {!selectChapters ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Please first Choose Chapter
                      </p>
                    ) : isLoading ? (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "green",
                        }}
                      >
                        Loading questions numbers...
                      </p>
                    ) : numberOfQuestion?.length > 0 ? (
                      numberOfQuestion?.map((item, i) => (
                        <p
                          key={i}
                          className="option_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuestion(item);
                            setOpenDropdown(null);
                          }}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        style={{
                          textAlign: "center",
                          marginTop: "35%",
                          color: "red",
                        }}
                      >
                        No of Questions found
                      </p>
                    )}
                  </section>
                )}
              </section>

              <button
                className="start_quiz_wrapper"
                disabled={!isStartEnabled}
                onClick={() => setShowSetupPopup(true)}
              >
                {/* Start quiz */}
                Evaluate Me
              </button>
            </section>
          </section>
        )}

        {/* quiz */}
        <section className="chat-panal">
          {quizInitial && !joinQuiz && (
            <section className="quiz_panal_choose_wrapper">
              <section className="quiz_choose_mode_headings">
                <h3>
                  Choose Your <span>Quiz</span> Mode
                </h3>
                <p>
                  Practice at your pace or join a live quiz with others. Select
                  the mode that fits your learning style right now.
                </p>
              </section>
              <section className="quiz_panal_mode_containers_wrapper">
                <section className="quiz_panal_mode_container">
                  <h3>Practice MCQs</h3>
                  <p>
                    Solve questions at your own pace, review your answers, and
                    steadily improve your knowledge without any time pressure.
                  </p>
                  <section className="practice_static_wrapper">
                    {PRACTICE_CONTENT.map((item) => {
                      return (
                        <section className="practice_static">
                          <img src={assets.ic_timer_blue} alt="ic_timer_blue" />
                          <h4>{item}</h4>
                        </section>
                      );
                    })}
                  </section>
                  <section
                    className="practice_btn_wrapper"
                    onClick={() => setQuizInitial(false)}
                  >
                    <h4>Practice Now</h4>
                    <img src={assets.ic_right_arrow} />
                  </section>
                </section>
                <section className="quiz_panal_mode_container">
                  <h3>Join Quiz Room</h3>
                  <p>
                    Compete with others in real-time quizzes hosted by a
                    teacher. Test your speed and accuracy against peers.
                  </p>
                  <section className="practice_static_wrapper">
                    {JOIN_ROOM_CONTENT.map((item) => {
                      return (
                        <section className="practice_static">
                          <img src={assets.ic_timer_blue} alt="ic_timer_blue" />
                          <h4>{item}</h4>
                        </section>
                      );
                    })}
                  </section>
                  <section
                    className="practice_btn_wrapper"
                    onClick={() => {
                      setJoinQuiz(true);
                      console.log("called");
                    }}
                  >
                    <h4>Join Room</h4>
                    <img src={assets.ic_right_arrow} />
                  </section>
                </section>
              </section>
            </section>
          )}

          {!quizInitial && !joinQuiz && (
            <Fragment>
              <div className="quiz-header-premium">
                <div className="header-left">
                  <button
                    className="back-btn"
                    onClick={() => {
                      if (questions.length === 0) {
                        setQuizInitial(true);
                      } else {
                        onCancel();
                      }
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                  </button>
                  <div className="breadcrumbs">
                    <h3>{selectedSubject || "Objective Test"}</h3>
                    <p>{selectedChapter}</p>
                  </div>
                </div>

                {questions.length > 0 && (
                  <div className="header-right">
                    <div className="question-counter">
                      <span className="label">QUESTION</span>
                      <span className="count">
                        <b>{String(currentQIndex + 1).padStart(2, "0")}</b> /{" "}
                        {totalQuestions}
                      </span>
                    </div>
                    {!isReviewMode &&
                      !markDetails?.DETAILS?.length &&
                      questions?.length > 0 && (
                        <div className="nav_hslider_dots">
                          {questions.map((_, i) => (
                            <span
                              key={i}
                              className={`hslider_dot${i === currentQIndex ? " active" : ""}`}
                              onClick={() => setCurrentQIndex(i)}
                            />
                          ))}
                        </div>
                      )}
                    <div className="timer-pill-premium">
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{formatTime(timeRemaining)}</span>
                    </div>
                  </div>
                )}
              </div>

              <section className="chat_body_quiz">
                <section className="quiz-main-container">
                  {questions.length === 0 && !markDetails?.DETAILS?.length ? (
                    <QuizHero />
                  ) : (
                    <Fragment>
                      {showPaletteModal ? (
                        <QuestionPalettePage
                          questions={questions}
                          selectedAnswers={selectedAnswers}
                          getQuestionStatus={getQuestionStatus}
                          setCurrentQIndex={setCurrentQIndex}
                          setShowPaletteModal={setShowPaletteModal}
                          setIsQuizActive={setIsQuizActive}
                          onSubmitQuiz={onSubmitQuiz}
                          submitLoading={submitLoading}
                          setIsReviewMode={setIsReviewMode}
                        />
                      ) : showMarks && markDetails?.DETAILS?.length ? (
                        <Fragment>
                          <section className="award_container">
                            <img
                              src={assets.ic_award}
                              alt="award"
                              className="award_img"
                            />
                            <h6 className="compelete_text">
                              You've successfully completed the assessment
                            </h6>
                            <h5 className="select_chapter">
                              {selectedChapter}
                            </h5>
                            <div className="syllabus">
                              <span className="badge_pill">
                                {user?.data?.board}
                              </span>
                              <span className="dot_sep"></span>
                              <span className="badge_pill subject">
                                {selectedSubject}
                              </span>
                            </div>
                            <section className="score_box_container">
                              <section className="score_box_wrapper">
                                <img src={assets.ic_score} alt="ic_score" />
                                <h3>{`${scorePercentage}%`}</h3>
                                <h5>Score</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img src={assets.ic_correct} alt="ic_score" />
                                <h3>{correctCount}</h3>
                                <h5>Correct</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img src={assets.ic_wrong} alt="ic_wrong" />
                                <h3>{wrongCount}</h3>
                                <h5>In Correct</h5>
                              </section>
                              <section className="score_box_wrapper">
                                <img
                                  src={assets.ic_unattempt}
                                  alt="ic_unattempt"
                                />
                                <h3>{notAnswerdCount}</h3>
                                <h5>Not Attempted</h5>
                              </section>
                            </section>
                            <section className="questions_overview_wrapper">
                              <h4>Question by Question Review</h4>
                              <h6>Detailed breakdown of each question</h6>
                              {markDetails?.DETAILS.map(renderScoreCardDetails)}
                            </section>
                            <section className="quiz_btns_wrapper">
                              <button
                                className="retake_btn"
                                onClick={onRetakeQuiz}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                <span>Retake Quiz</span>
                              </button>
                              <button
                                className="back_menu_btn"
                                onClick={onCancel}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                  />
                                </svg>
                                <span>Back to Main Menu</span>
                              </button>
                            </section>
                          </section>
                        </Fragment>
                      ) : (
                        <div
                          className={
                            isReviewMode
                              ? "quiz_review_layout"
                              : "quiz_normal_layout"
                          }
                        >
                          <div className="review_main_content">
                            {(() => {
                              const showStack =
                                !isReviewMode &&
                                !markDetails?.DETAILS?.length &&
                                questions?.length > 0;

                              if (showStack) {
                                const q = questions[currentQIndex];
                                const selOpt = selectedAnswers[currentQIndex];

                                return (
                                  <QuizHSlider
                                    questions={questions}
                                    renderCard={(qIdx) => {
                                      const qItem = questions[qIdx];
                                      const sOpt = selectedAnswers[qIdx];
                                      const isAns = sOpt !== undefined;
                                      const isCor =
                                        isAns &&
                                        qItem?.options[sOpt] === qItem?.answer;
                                      return (
                                        <QuestionCardContent
                                          q={qItem}
                                          qIndex={qIdx}
                                          currentQIndex={currentQIndex}
                                          isFullySubmitted={isFullySubmitted}
                                          isAnsweredQ={isAns}
                                          isCorrectQ={isCor}
                                          selOpt={sOpt}
                                          mode={mode}
                                          handleOptionSelect={
                                            handleOptionSelect
                                          }
                                          showStack={showStack}
                                          isReviewMode={isReviewMode}
                                          setCurrentQIndex={setCurrentQIndex}
                                          setShowPaletteModal={
                                            setShowPaletteModal
                                          }
                                          questions={questions}
                                          questionDetails={questionDetails}
                                        />
                                      );
                                    }}
                                    currentQIndex={currentQIndex}
                                    setCurrentQIndex={setCurrentQIndex}
                                    onEndQuiz={() => setShowPaletteModal(true)}
                                    endIcon={assets.ic_arrow_white}
                                  />
                                );
                              }

                              if (
                                isReviewMode ||
                                (!markDetails?.DETAILS?.length &&
                                  questionDetails.length > 0)
                              ) {
                                const q = questions[currentQIndex];
                                const selOpt = selectedAnswers[currentQIndex];
                                const isAnsweredQ = selOpt !== undefined;
                                const isCorrectQ =
                                  isAnsweredQ &&
                                  q?.options[selOpt] === q?.answer;

                                return (
                                  <section
                                    className="quiz-container"
                                    key={animKey}
                                  >
                                    <section
                                      className={`quiz-main slide-in-${slideDirection}`}
                                    >
                                      <QuestionCardContent
                                        q={q}
                                        qIndex={currentQIndex}
                                        currentQIndex={currentQIndex}
                                        isFullySubmitted={isFullySubmitted}
                                        isAnsweredQ={isAnsweredQ}
                                        isCorrectQ={isCorrectQ}
                                        selOpt={selOpt}
                                        mode={mode}
                                        handleOptionSelect={handleOptionSelect}
                                        showStack={showStack}
                                        isReviewMode={isReviewMode}
                                        setCurrentQIndex={setCurrentQIndex}
                                        setShowPaletteModal={
                                          setShowPaletteModal
                                        }
                                        questions={questions}
                                        questionDetails={questionDetails}
                                        onZoomImage={setZoomModalImage}
                                      />
                                    </section>
                                  </section>
                                );
                              }

                              return null;
                            })()}
                          </div>

                          {isReviewMode && (
                            <QuestionPaletteSidebar
                              questions={questions}
                              selectedAnswers={selectedAnswers}
                              getQuestionStatus={getQuestionStatus}
                              currentQIndex={currentQIndex}
                              setCurrentQIndex={setCurrentQIndex}
                              isQuizSubmitted={isFullySubmitted}
                              onSubmitQuiz={() => {
                                setIsQuizActive(false);
                                onSubmitQuiz();
                              }}
                              submitLoading={submitLoading}
                            />
                          )}
                        </div>
                      )}
                    </Fragment>
                  )}
                </section>
              </section>
            </Fragment>
          )}

          {showJoinScreen && (
            <QuizJoinInitialContent
              roomCode={roomCode}
              nickName={nickName}
              onChangeRoom={onChangeRoom}
              waitingCall={waitingCall}
              setWaitingCall={setWaitingCall}
              joinRoom={joinRoom}
              setJoinQuiz={setJoinQuiz}
            />
          )}
          {waitingCall && (
            <QuizLobbyContent
              players={players}
              roomCode={roomCode}
              nickName={nickName}
              setJoinQuiz={setJoinQuiz}
              setWaitingCall={setWaitingCall}
            />
          )}
          {wsQuizStarted &&
            wsQuestion &&
            wsQuizFinalResults?.length === 0 &&
            !wsQuizEnded && (
              <section className="ws_quiz_wrapper">
                <div className="ws_quiz_container">
                  <div className="ws_quiz_header">
                    <div className="ws_question_count">
                      <span className="ws_count_label">Q</span>
                      <span className="ws_count_val">{wsQuestionIndex + 1} / {wsTotalQuestions}</span>
                    </div>

                    <div className="ws_timer_badge">
                      <span className="ws_timer_dot"></span>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>00:{String(wsTimeRemaining).padStart(2, "0")}</span>
                    </div>
                  </div>

                  <div className="ws_progress_wrapper">
                    <div
                      className="ws_progress_bar"
                      style={{
                        width: `${((wsQuestionIndex + 1) / wsTotalQuestions) * 100}%`,
                      }}
                    />
                  </div>

                  <div className="ws_question_title">
                    <LatexRenderer text={wsQuestion?.question} />
                  </div>

                  {wsQuestion?.imageUrls && wsQuestion.imageUrls.length > 0 && (
                    <div className="ws_q_image_container">
                      {wsQuestion.imageUrls.map((imgUrl, i) => (
                        <QuizImagePreview
                          key={i}
                          src={imgUrl}
                          alt={`Question Diagram ${i + 1}`}
                          title={`Question ${wsQuestionIndex + 1} Diagram`}
                          onZoom={setZoomModalImage}
                        />
                      ))}
                    </div>
                  )}

                  {(wsQuestion?.isShort || wsQuestion?.type === "SHORT" || wsQuestion?.questionType === "SHORT") ? (
                    <div className="ws_short_answer_wrapper" style={{ marginTop: "24px", marginBottom: "20px" }}>
                      <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, color: "#374151", fontSize: "14px" }}>
                        Type your answer below:
                      </label>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <textarea
                          rows={3}
                          className="ws_short_answer_input"
                          placeholder="Enter your answer here..."
                          disabled={wsSubmitted}
                          value={wsSelectedAnswer ?? ""}
                          onChange={(e) => setWsSelectedAnswer(e.target.value)}
                          style={{
                            flex: 1,
                            padding: "14px 18px",
                            fontSize: "16px",
                            border: "2px solid #612EF7",
                            borderRadius: "12px",
                            outline: "none",
                            backgroundColor: wsSubmitted ? "#F3F4F6" : "#FFFFFF",
                            color: "#1F2937",
                            minHeight: "90px",
                            maxHeight: "180px",
                            overflowY: "auto",
                            resize: "vertical",
                            fontFamily: "inherit",
                            lineHeight: "1.5",
                          }}
                        />
                        <button
                          type="button"
                          disabled={wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === ""}
                          onClick={() => handleWsAnswer(wsSelectedAnswer)}
                          style={{
                            padding: "14px 24px",
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            backgroundColor: (wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === "") ? "#9CA3AF" : "#612EF7",
                            borderRadius: "12px",
                            border: "none",
                            cursor: (wsSubmitted || !wsSelectedAnswer || String(wsSelectedAnswer).trim() === "") ? "not-allowed" : "pointer",
                          }}
                        >
                          {wsSubmitted ? "Submitted" : "Submit"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ws_options_wrapper">
                      {wsQuestion?.options?.map((option, index) => {
                        const optionLetter = String.fromCharCode(65 + index);
                        const optImg = wsQuestion?.optionImages?.[optionLetter];
                        const isSelected = wsSelectedAnswer === index;

                        return (
                          <button
                            key={index}
                            onClick={() => handleWsAnswer(index)}
                            className={`ws_option_btn ${isSelected ? "active" : ""}`}
                          >
                            <div className="ws_option_left">
                              <div className={`ws_badge ${isSelected ? "active" : ""}`}>
                                {optionLetter}
                              </div>

                              <div className="ws_opt_content">
                                {option && option.trim() !== "" && (
                                  <span className="ws_option_text">
                                    <LatexRenderer text={option} />
                                  </span>
                                )}
                                {optImg && (
                                  <div className="ws_opt_img_wrapper">
                                    <QuizImagePreview
                                      src={optImg}
                                      alt={`Option ${optionLetter}`}
                                      title={`Question ${wsQuestionIndex + 1} - Option ${optionLetter}`}
                                      isOption
                                      onZoom={setZoomModalImage}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className={`ws_radio ${isSelected ? "checked" : ""}`}>
                              {isSelected && <div className="ws_radio_inner"></div>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            )}

          {showAdminRoundResult && (
            <CommonPopup
              open={showAdminRoundResult}
              onClose={() => setShowAdminRoundResult(false)}
              url={true}
            >
              <AdminRoundResult data={adminRoundResult} />
            </CommonPopup>
          )}

          {
            // (wsQuizEnded && wsQuizFinalResults?.length > 0) ||
            showAsyncLeaderboard &&
              asyncLeaderboard?.length > 0 &&
              !showAsyncHistory && (
                <section className="quiz_leader_wrapper">
                  <div className="quiz_leader_board_main_heading">
                    <div className="leaderboard_trophy_badge">
                      <span>🏆 Final Standings</span>
                    </div>
                    <h3>
                      Final <span>Scores</span>
                    </h3>
                    <h5>Incredible performance! Here are the final results.</h5>
                  </div>

                  <div className="leaderboard">
                    {leaderboardData?.slice(0, 3).map((user, index) => (
                      <div className={`card position-${index + 1}`} key={index}>
                        <div className="avatar-wrapper">
                          <img
                            src={assets.ic_chat_person}
                            alt={user?.nickname}
                          />
                          <span className={`badge rank-${index + 1}`}>
                            #{index + 1}
                          </span>
                        </div>

                        <div className="card-content">
                          <h3>{user?.nickname}</h3>
                          <p className="score">{user?.score} pts</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="quiz_leader_board_table_wrapper">
                    <div className="quiz_leader_board_header">
                      <div className="leader_board_sub_header">
                        <h4>Rank</h4>
                        <h4>Player</h4>
                      </div>
                      <h4>Score</h4>
                    </div>

                    {leaderboardData?.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="quiz_leader_board_card_container"
                        >
                          <div className="leader_board_no">
                            <h4>{index + 1}</h4>
                          </div>

                          <div className="leader_board_inner_wrapper">
                            <img
                              src={assets.ic_chat_person}
                              alt="ic_chat_person"
                            />
                            <h4>{item?.nickname}</h4>
                          </div>

                          <div className="leader_board_score">
                            <h4>{item?.score} pts</h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="quiz_leader_board_btns_wrapper">
                    <button
                      className="leader_board_quiz_btn"
                      onClick={() => {
                        setShowAsyncHistory(true);
                        apiJoinRoomHistoryDetails();
                      }}
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Quiz History</span>
                    </button>

                    <button
                      className="leader_board_new_room_btn"
                      onClick={() => {
                        // live reset
                        setJoinQuiz(true);
                        setWsQuizEnded(false);
                        setWsQuizStarted(false);
                        setWsQuestion(null);
                        setWsFinalScores([]);
                        setWaitingCall(false);
                        setPlayers([]);
                        setWsSelectedAnswer(null);

                        // async reset
                        setIsAsyncQuiz(false);
                        setAsyncQuestions([]);
                        setAsyncCurrentIndex(0);
                        setAsyncSelectedAnswers({});
                        setShowReviewPage(false);
                        setAsyncLeaderboard([]);
                        setShowAsyncLeaderboard(false);
                        setShowAsyncHistory(false);

                        // reset room
                        setRoomDetails({
                          roomCode: "",
                          nickName: "",
                        });
                        document.body.classList.remove("hide_sidebar");
                        if (document.fullscreenElement) {
                          document.exitFullscreen().catch(() => {});
                        }
                      }}
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                      </svg>
                      <span>New Room</span>
                    </button>
                  </div>
                </section>
              )
          }

          {isAsyncQuiz && asyncCurrentQuestion && !showReviewPage && (
            <section className="ws_quiz_wrapper">
              <div className="ws_quiz_container">
                <div className="ws_quiz_header">
                  <div className="ws_question_count">
                    <span className="ws_count_label">Q</span>
                    <span className="ws_count_val">{asyncCurrentIndex + 1} / {wsTotalQuestions}</span>
                  </div>

                  <div className="ws_question_step_badge">
                    Question {asyncCurrentIndex + 1} of {wsTotalQuestions}
                  </div>
                </div>

                <div className="ws_progress_wrapper">
                  <div
                    className="ws_progress_bar"
                    style={{
                      width: `${((asyncCurrentIndex + 1) / wsTotalQuestions) * 100}%`,
                    }}
                  />
                </div>

                <div className="ws_question_title">
                  <LatexRenderer text={asyncCurrentQuestion?.question} />
                </div>

                {asyncCurrentQuestion?.imageUrls && asyncCurrentQuestion.imageUrls.length > 0 && (
                  <div className="ws_q_image_container">
                    {asyncCurrentQuestion.imageUrls.map((imgUrl, i) => (
                      <QuizImagePreview
                        key={i}
                        src={imgUrl}
                        alt={`Question Diagram ${i + 1}`}
                        title={`Question ${asyncCurrentIndex + 1} Diagram`}
                        onZoom={setZoomModalImage}
                      />
                    ))}
                  </div>
                )}

                {(asyncCurrentQuestion?.isShort || asyncCurrentQuestion?.type === "SHORT" || asyncCurrentQuestion?.questionType === "SHORT") ? (
                  <div className="ws_short_answer_wrapper" style={{ marginTop: "24px", marginBottom: "20px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, color: "#374151", fontSize: "14px" }}>
                      Type your answer below:
                    </label>
                    <textarea
                      rows={3}
                      className="ws_short_answer_input"
                      placeholder="Enter your answer here..."
                      value={asyncSelectedAnswers[asyncCurrentIndex] ?? ""}
                      onChange={(e) => handleAsyncAnswer(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "14px 18px",
                        fontSize: "16px",
                        border: "2px solid #612EF7",
                        borderRadius: "12px",
                        outline: "none",
                        backgroundColor: "#FFFFFF",
                        color: "#1F2937",
                        boxShadow: "0 2px 4px rgba(97, 46, 247, 0.08)",
                        minHeight: "90px",
                        maxHeight: "180px",
                        overflowY: "auto",
                        resize: "vertical",
                        fontFamily: "inherit",
                        lineHeight: "1.5",
                      }}
                    />
                  </div>
                ) : (
                  <div className="ws_options_wrapper">
                    {asyncCurrentQuestion?.options?.map((option, index) => {
                      const optionLetter = String.fromCharCode(65 + index);
                      const optImg = asyncCurrentQuestion?.optionImages?.[optionLetter];
                      const isSelected = asyncSelectedAnswers[asyncCurrentIndex] === index;

                      return (
                        <button
                          key={index}
                          onClick={() => handleAsyncAnswer(index)}
                          className={`ws_option_btn ${isSelected ? "active" : ""}`}
                        >
                          <div className="ws_option_left">
                            <div className={`ws_badge ${isSelected ? "active" : ""}`}>
                              {optionLetter}
                            </div>

                            <div className="ws_opt_content">
                              {option && option.trim() !== "" && (
                                <span className="ws_option_text">
                                  <LatexRenderer text={option} />
                                </span>
                              )}
                              {optImg && (
                                <div className="ws_opt_img_wrapper">
                                  <QuizImagePreview
                                    src={optImg}
                                    alt={`Option ${optionLetter}`}
                                    title={`Question ${asyncCurrentIndex + 1} - Option ${optionLetter}`}
                                    isOption
                                    onZoom={setZoomModalImage}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          <div className={`ws_radio ${isSelected ? "checked" : ""}`}>
                            {isSelected && <div className="ws_radio_inner"></div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="ws_footer_actions">
                  <div className="ws_footer_hint">
                    {asyncSelectedAnswers[asyncCurrentIndex] !== undefined && String(asyncSelectedAnswers[asyncCurrentIndex]).trim() !== "" ? (
                      <span className="ws_hint_selected">
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        {(asyncCurrentQuestion?.isShort || asyncCurrentQuestion?.type === "SHORT") ? "Answer entered" : "Option selected"}
                      </span>
                    ) : (
                      <span className="ws_hint_unselected">
                        {(asyncCurrentQuestion?.isShort || asyncCurrentQuestion?.type === "SHORT") ? "Enter answer to proceed" : "Select an option to proceed"}
                      </span>
                    )}
                  </div>

                  <button className="ws_next_btn" onClick={handleAsyncNext}>
                    <span>
                      {editingFromReview
                        ? "Back to Review"
                        : asyncCurrentIndex === (wsTotalQuestions ? wsTotalQuestions - 1 : asyncQuestions.length - 1)
                        ? "Review Answers"
                        : "Next Question"}
                    </span>
                    <div className="ws_btn_icon">
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </section>
          )}

          {showReviewPage && (
            <QuizReviewContent
              questions={asyncQuestions}
              selectedAnswers={asyncSelectedAnswers}
              setAsyncCurrentIndex={setAsyncCurrentIndex}
              setShowReviewPage={setShowReviewPage}
              setEditingFromReview={setEditingFromReview}
              onSubmitQuiz={onSubmitQuiz}
              showReviewPage={showReviewPage}
              asyncQuizSubmit={asyncQuizSubmit}
            />
          )}

          {showAsyncHistory && (
            <QuizHistroyDetails
              historyData={quizHistorySessions}
              onBack={() => setShowAsyncHistory(false)}
            />
          )}

          {isNeedsFullscreen && (
            <LiveQuizFullscreenGuard onReEnter={tryFullscreen} />
          )}

          {showRulesModal && (
            <AntiCheatRulesModal
              maxWarnings={3}
              onStart={acknowledgeRules}
            />
          )}

          {showAntiCheatModal && (
            <AntiCheatModal
              warnings={antiCheatWarnings}
              maxWarnings={3}
              message={antiCheatMessage}
              onResume={reEnterFullscreen}
            />
          )}

          {isAutoSubmitted && (
            <AutoSubmittedModal
              reason={autoSubmitReason}
              onClose={closeAutoSubmitModal}
            />
          )}

          <ImageZoomModal
            image={zoomModalImage}
            onClose={() => setZoomModalImage(null)}
          />
        </section>
      </section>
    </Fragment>
  );
};
